import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { MemoryRouter, useInRouterContext } from 'react-router-dom';
import CommunityFeed from '../../pages/CommunityFeed';
import CommunityRightRail from '../community/CommunityRightRail';
import { RIGHT_RAIL_WIDTH } from '../layout/rightRailTokens';

function SafeCommunityFeed(props) {
  const inRouter = useInRouterContext();
  if (inRouter) return <CommunityFeed {...props} />;
  return (
    <MemoryRouter>
      <CommunityFeed {...props} />
    </MemoryRouter>
  );
}

/**
 * LinkedIn-style community home: feed column + sticky right rail (md+).
 * No section title — feed starts immediately (compose + posts).
 * Practice CTAs live in the right-rail Shortcuts.
 * `afterPlacementSlot` sits in the rail after Placement prep.
 */
export default function CommunitySnapshot({
  onNavigateToDiscover,
  onNavigateToFeed,
  onNavigateToMarketplace,
  onStartInterview,
  onReviewLast,
  lastInterviewId = null,
  leadSlot = null,
  afterPlacementSlot = null,
}) {
  return (
    <Box
      sx={{
        mb: 4,
        width: '100%',
        maxWidth: '100%',
      }}
      data-testid="community-snapshot"
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'flex-start' },
          gap: { xs: 2, md: 1.25 },
          justifyContent: { md: 'flex-start' },
          width: '100%',
        }}
      >
        <Box
          data-testid="community-feed-column"
          sx={{
            flex: { xs: '1 1 auto', md: '1 1 0' },
            width: '100%',
            minWidth: 0,
            // No horizontal padding — posts/media own the column edges
            px: 0,
          }}
        >
          {leadSlot && (
            <Box sx={{ px: { xs: 1.5, sm: 0 }, mb: 1.5 }}>
              {leadSlot}
            </Box>
          )}

          <Box sx={{ p: 0, m: 0, width: '100%' }}>
            <SafeCommunityFeed isDashboardGlimpse />
          </Box>
        </Box>

        <Box
          sx={{
            px: { xs: 1.5, sm: 0 },
            width: { xs: '100%', md: RIGHT_RAIL_WIDTH },
            maxWidth: { md: RIGHT_RAIL_WIDTH },
            flex: { md: `0 0 ${RIGHT_RAIL_WIDTH}px` },
            flexShrink: 0,
            minWidth: 0,
            // Layout only — sticky lives on RightRail so we don't nest sticky offsets
            alignSelf: { md: 'flex-start' },
            display: { xs: 'none', md: 'block' },
            pr: { md: 1 },
            mt: 0,
            pt: 0,
          }}
          data-testid="community-rail-column"
          data-rail-width={RIGHT_RAIL_WIDTH}
        >
          <CommunityRightRail
            onNavigateToDiscover={onNavigateToDiscover}
            onNavigateToFeed={onNavigateToFeed}
            onNavigateToMarketplace={onNavigateToMarketplace}
            onStartInterview={onStartInterview}
            onReviewLast={onReviewLast}
            lastInterviewId={lastInterviewId}
            showPractice={Boolean(onStartInterview)}
            afterPlacementSlot={afterPlacementSlot}
          />
        </Box>
      </Box>
    </Box>
  );
}

CommunitySnapshot.propTypes = {
  onNavigateToDiscover: PropTypes.func,
  onNavigateToFeed: PropTypes.func,
  onNavigateToMarketplace: PropTypes.func,
  onStartInterview: PropTypes.func,
  onReviewLast: PropTypes.func,
  lastInterviewId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  leadSlot: PropTypes.node,
  afterPlacementSlot: PropTypes.node,
};
